import React from 'react';
import { Box, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';

export interface HorizontalBarItem {
    /** Row label shown on the left. */
    label: string;
    /** Bar fill width as a percentage 0–100. */
    percentage: number;
    /** Value displayed to the right of the bar (e.g. '7.2 / 10' or '85%'). */
    displayValue: string;
    /** Smaller secondary text on the far right (e.g. '120 responses'). */
    caption?: string;
    /**
     * One line shown on hover over the value, for the context behind it (e.g. how many
     * answers the percentage is based on). Omit it and no tooltip is attached.
     */
    tooltip?: string;
    /** Overrides the chart-level barColor for this row. */
    barColor?: string;
    questiontext?: string | null;
}

interface HorizontalBarChartProps {
    rows: HorizontalBarItem[];
    /** Default bar color for all rows — MUI palette path or CSS color. Default: 'primary.main'. */
    barColor?: string;
    /** Unfilled track color — MUI palette path or CSS color. Default: 'grey.100'. */
    trackColor?: string;
    /** Color for the value text. Defaults to the bar color, so per-row colors carry over. */
    valueColor?: string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
    rows,
    barColor = 'primary.dark',
    trackColor = 'grey.100',
    valueColor,
}) => (
    <Stack spacing={1}>
        {rows.map((row) => {
            const fill = row.barColor ?? barColor;
            const valueFill = valueColor ?? fill;
            const hasCaption = Boolean(row.caption);

            return (
                <Box
                    key={row.label}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: hasCaption ? '160px 1fr auto auto' : '160px 1fr auto',
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="body2" noWrap title={row.questiontext ? row.questiontext : row.label}>
                        {row.label}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, row.percentage))}
                            sx={{
                                flex: 1,
                                height: 9,
                                borderRadius: 999,
                                bgcolor: trackColor,
                                '& .MuiLinearProgress-bar': { bgcolor: fill, borderRadius: 999 },
                            }}
                        />
                    </Box>

                    {/* An empty title renders no tooltip, so a row without one is unaffected. */}
                    <Tooltip title={row.tooltip ?? ''} arrow placement="top">
                        <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ textAlign: 'right', color: valueFill, cursor: row.tooltip ? 'help' : undefined }}
                        >
                            {row.displayValue}
                        </Typography>
                    </Tooltip>

                    {hasCaption && (
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
                            {row.caption}
                        </Typography>
                    )}
                </Box>
            );
        })}
    </Stack>
);

export default HorizontalBarChart;
